import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ShoppingBag as ShoppingBagIcon, Favorite as FavoriteIcon } from '@mui/icons-material';
import AdSlot from './AdSlot';
import { AdError, TargetingData } from '../../types/ads';
import { targetingService, TargetingData as ServiceTargetingData } from '../../services/TargetingService';
import { adManager } from '../../services/AdManager';

interface NativeInFeedAdProps {
  className?: string;
  targeting?: TargetingData;
  index?: number; // Position in the feed for targeting
  onLoad?: () => void;
  onError?: (error: AdError) => void;
  onInteraction?: (action: string) => void;
}

/**
 * Native In-Feed Ad component for outfit card integration.
 *
 * Rendering strategy (TikTok-style):
 * 1. Immediately show the native ad card with content from the ad server.
 * 2. Attempt to load a programmatic ad via AdSlot (Prebid → GAM → Revive).
 * 3. If the programmatic ad loads, replace the native content with the served creative.
 * 4. If it fails or times out, keep showing the native card — the ad placement is
 *    always visible so the user sees the "Sponsored" card in the feed.
 */
const NativeInFeedAd: React.FC<NativeInFeedAdProps> = ({
  className = '',
  targeting = {},
  index = 0,
  onLoad,
  onError,
  onInteraction
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [contextualTargeting, setContextualTargeting] = useState<TargetingData>({});
  const [programmaticLoaded, setProgrammaticLoaded] = useState(false);
  const adContainerRef = useRef<HTMLDivElement>(null);
  const impressionTracked = useRef(false);

  // Generate contextual targeting for native in-feed placement
  useEffect(() => {
    const generateTargeting = async () => {
      try {
        const pageType = window.location.pathname.includes('/search')
          ? 'search'
          : window.location.pathname.includes('/profile')
          ? 'profile'
          : 'home';
        const nativeTargeting = await targetingService.generateTargeting({
          pageContext: pageType,
          pageType: pageType
        });

        const adsTargeting: TargetingData = {
          keywords: ['fashion', 'native', 'feed'],
          categories: ['fashion', 'native'],
          demographics: {
            ageRange: Array.isArray(nativeTargeting.demographics?.ageRange)
              ? `${nativeTargeting.demographics.ageRange[0]}-${nativeTargeting.demographics.ageRange[1]}`
              : nativeTargeting.demographics?.ageRange || '18-65',
            gender: nativeTargeting.demographics?.gender || 'all',
            location: nativeTargeting.demographics?.location || 'global'
          },
          contextual: {
            pageType: pageType as 'home' | 'search' | 'profile',
            fashionStyles: [],
            brands: [],
            events: []
          }
        };

        setContextualTargeting(adsTargeting);
      } catch (error) {
        console.error('Error generating native in-feed targeting:', error);
        setContextualTargeting({});
      }
    };

    generateTargeting();
  }, [index]);

  // Track impression when the card enters the viewport
  useEffect(() => {
    if (!adContainerRef.current || impressionTracked.current) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !impressionTracked.current) {
            impressionTracked.current = true;
            console.log(`📢 Native in-feed ad ${index} impression tracked`);
            // Track via gtag if available (no API call needed)
            if (window.gtag) {
              window.gtag('event', 'ad_impression', {
                event_category: 'native_ad',
                event_label: `native-in-feed-${index}`,
                ad_slot: `native-in-feed-${index}`
              });
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(adContainerRef.current);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle programmatic ad load (Prebid/GAM/Revive served a creative)
  const handleProgrammaticLoad = useCallback(() => {
    setProgrammaticLoaded(true);
    onLoad?.();
    console.log(`📢 Native in-feed ad ${index}: programmatic creative loaded`);
  }, [index, onLoad]);

  // Handle programmatic ad error — we still show the native card
  const handleProgrammaticError = useCallback((error: AdError) => {
    console.log(`📢 Native in-feed ad ${index}: programmatic failed, showing native card`, error.message);
    onError?.(error);
  }, [index, onError]);

  // Handle user interactions
  const handleInteraction = useCallback((action: string) => {
    onInteraction?.(action);

    if (window.gtag) {
      window.gtag('event', 'ad_interaction', {
        event_category: 'native_ad',
        event_label: action,
        ad_slot: 'native-in-feed',
        position: index
      });
    }
  }, [index, onInteraction]);

  // Merge targeting data
  const finalTargeting: TargetingData = {
    ...contextualTargeting,
    ...targeting,
    keywords: [
      ...(contextualTargeting.keywords || []),
      ...(targeting.keywords || []),
      'fashion', 'native', 'feed', 'outfit', 'style'
    ],
    categories: [
      ...(contextualTargeting.categories || []),
      ...(targeting.categories || []),
      'native', 'in-feed', 'fashion', 'discovery'
    ],
    contextual: {
      ...contextualTargeting.contextual,
      ...targeting.contextual,
      pageType: window.location.pathname.includes('/search') ? 'search' : 'home'
    }
  };

  const adSizes: [number, number][] = [
    [300, 250],
    [300, 400],
    [320, 480],
    [1, 1]
  ];

  return (
    <Card
      ref={adContainerRef}
      className={`native-in-feed-ad ${className}`}
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        boxShadow: theme.shadows[2],
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[6]
        }
      }}
    >
      {/* Sponsored label — always visible */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '10px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}
      >
        Sponsored
      </Box>

      {/*
        Programmatic ad container — visible so the AdSlot's IntersectionObserver
        can fire. When a Prebid/GAM/Revive creative loads, it renders here and
        we hide the native fallback below.
      */}
      <Box
        sx={{
          display: programmaticLoaded ? 'block' : 'none',
          minHeight: programmaticLoaded ? 'auto' : 0
        }}
      >
        <AdSlot
          slotId={`native-in-feed-${index}`}
          sizes={adSizes}
          targeting={finalTargeting}
          lazy={false}
          refreshInterval={0}
          className="native-in-feed-ad-slot"
          onLoad={handleProgrammaticLoad}
          onError={handleProgrammaticError}
        />
      </Box>

      {/*
        Native ad card content — always rendered as the visible fallback.
        Hidden only when a programmatic creative successfully loads above.
        This ensures the ad placement is always visible in the feed.
      */}
      {!programmaticLoaded && (
        <>
          {/* Ad image area with gradient overlay */}
          <Box
            sx={{
              position: 'relative',
              height: isMobile ? 200 : 280,
              background: `linear-gradient(135deg, ${theme.palette.primary.light}22, ${theme.palette.secondary.light}33)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden'
            }}
            onClick={() => handleInteraction('image_click')}
          >
            {/* Decorative fashion icon */}
            <Box sx={{ textAlign: 'center', opacity: 0.9 }}>
              <ShoppingBagIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}
              >
                Fashion Partner
              </Typography>
            </Box>
          </Box>

          {/* Ad content */}
          <CardContent sx={{ p: 2 }}>
            {/* Brand and price row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Sponsored · Fashion Partner
              </Typography>
              <Chip label="Shop" size="small" color="primary" variant="outlined" />
            </Box>

            {/* Title */}
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                lineHeight: 1.3,
                cursor: 'pointer'
              }}
              onClick={() => handleInteraction('title_click')}
            >
              Discover Your Perfect Style
            </Typography>

            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.4 }}>
              Curated fashion picks for every occasion
            </Typography>

            {/* CTA buttons */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<ShoppingBagIcon />}
                onClick={() => handleInteraction('cta_click')}
                sx={{ flexGrow: 1, textTransform: 'none', fontWeight: 600 }}
              >
                Shop Now
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FavoriteIcon />}
                onClick={() => handleInteraction('favorite_click')}
                sx={{ minWidth: 'auto', px: 1 }}
                aria-label="Save to favorites"
              >
                Save
              </Button>
            </Box>
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default NativeInFeedAd;
